// Profile API helpers (wiki/feature-user-profile.md). Name, avatar, and
// preferred-WPM updates plus the signed avatar upload/display URLs. All go
// through the shared `api` client — no hand-rolled fetch.
//
// The three updates are `DomainCommand`s: `id` is the aggregate root — the
// SIGNED-IN user's own id, since the User stream being written IS the caller.
// The caller passes it from the auth store (`$auth.user.id`), the same pattern
// as impersonation.ts / progress.ts. The server owner-stamps `Id` from the JWT
// before dispatch, so a body id can never target another user's stream.

import { api } from './client';
import { putToSignedUrl } from './uploads';
import type {
  UserId,
  DownloadUrlResponse,
  IssueUploadUrlResponse,
  ProfileUpdateCommand,
  SetAvatarCommand,
  SetPreferredWpmCommand,
} from '@voicedojo/backend-types';

/** PUT /api/user/profile — set the single display name. */
export async function updateName(userId: UserId, name: string): Promise<void> {
  const command: ProfileUpdateCommand = { id: userId, name };
  await api.put<unknown>('/user/profile', command, { requiresAuth: true });
}

/** PUT /api/user/avatar — set the avatar (stock id or uploaded object-key). */
export async function setAvatar(userId: UserId, avatar: string): Promise<void> {
  const command: SetAvatarCommand = { id: userId, avatar };
  await api.put<unknown>('/user/avatar', command, { requiresAuth: true });
}

/** PUT /api/user/preferred-wpm — set the preferred speaking rate. */
export async function setPreferredWpm(userId: UserId, preferredWpm: number): Promise<void> {
  const command: SetPreferredWpmCommand = { id: userId, preferredWpm };
  await api.put<unknown>('/user/preferred-wpm', command, { requiresAuth: true });
}

/** POST /api/user/avatar/upload-url — mint a signed PUT URL for an avatar image. */
export async function requestAvatarUploadUrl(contentType: string): Promise<IssueUploadUrlResponse> {
  return api.post<IssueUploadUrlResponse>(
    '/user/avatar/upload-url',
    { contentType },
    { requiresAuth: true },
  );
}

/** POST /api/user/avatar/url — signed GET URL for the caller's uploaded avatar. */
export async function requestAvatarUrl(): Promise<DownloadUrlResponse> {
  return api.post<DownloadUrlResponse>('/user/avatar/url', {}, { toast: false, requiresAuth: true });
}

/**
 * Uploads an avatar image: mints a signed PUT URL, PUTs the bytes directly to
 * object storage, then sets the avatar to the returned object-key. Returns the
 * object-key on success.
 */
export async function uploadAvatar(userId: UserId, file: File): Promise<string> {
  const { uploadUrl, objectKey } = await requestAvatarUploadUrl(file.type);
  await putToSignedUrl(uploadUrl, file, file.type);
  await setAvatar(userId, objectKey);
  return objectKey;
}
